import { z } from "zod";
import { bottleFormatSchema } from "./lot.js";
import { wineSummarySchema } from "./wine.js";
/**
 * What should be carried from the ageing cabinet to the serving one.
 *
 * Task `8.12`. `project-scope.md` calls this the one piece of physical cellar
 * work the application can actually initiate, and the shape of the answer
 * follows from that: it is not a list of wines, it is a list of **objects to
 * pick up**, each with the shelf it is standing on and somewhere to put it.
 *
 * **Assembled by the server, decided by the rule in `shared/`.** The browser
 * could not assemble it — the wine list says which cabinet a bottle stands in
 * by name and not by purpose, and it carries no bottle ids, which is what a
 * move needs. So the route does the join and applies
 * `wantedAtServingTemperature` to the same urgency the *Att öppna* page reads,
 * and the page groups what comes back with the same `groupByUrgency` it uses.
 * One rule, and the two pages cannot disagree about which bottle is in a
 * hurry.
 *
 * **What it leaves out is on the page too.** The bottles that are waiting
 * behind this list, and the ones already standing in the serving cabinet, are
 * counted and not listed — a page that showed one bottle and said nothing else
 * would look broken on a cellar of 176.
 */
/**
 * One bottle to pick up, and where it is standing right now.
 *
 * The id travels because the move needs actual objects: bottles of one wine on
 * one shelf are indistinguishable to a person, and the history still has to
 * answer *where has this bottle been*. The format travels because a magnum is
 * a different thing to find room for than a bottle.
 */
export const servingBottleSchema = z.object({
    id: z.string(),
    format: bottleFormatSchema,
    storageName: z.string(),
    shelfName: z.string(),
});
/**
 * A wine with bottles worth carrying, as the whole wine rather than as a row.
 *
 * `wineSummarySchema` entire, and deliberately: the page draws the drinking
 * window band and says where each year came from, which is the answer to *why
 * this bottle* — and the list is a handful of rows, so the cost of carrying
 * the fields the row does not print is a rounding error against the cost of a
 * second shape that could drift from the first.
 */
export const servingCandidateSchema = wineSummarySchema.extend({
    bottles: z.array(servingBottleSchema),
});
/**
 * Somewhere to put a bottle: one shelf of one serving cabinet.
 *
 * `temperatureC` is the effective figure — the shelf's own where it has one,
 * the cabinet's where it does not — because that is the number the choice is
 * made against, and resolving it here means the rule that chooses a shelf
 * never has to know that a shelf sits inside a cabinet.
 *
 * `standing` and `capacity` are what the cabinet is for saying out loud.
 * Capacity warns and does not refuse, per `project-scope.md`, so this is what
 * the page warns *with*.
 */
export const servingShelfSchema = z.object({
    storageName: z.string(),
    shelfId: z.string(),
    shelfName: z.string(),
    temperatureC: z.number().int().nullable(),
    standing: z.number().int(),
    capacity: z.number().int().nullable(),
});
export const servingMovesResponseSchema = z.object({
    candidates: z.array(servingCandidateSchema),
    /** Every shelf in every `Serving` cabinet, cabinet by cabinet, in name order. */
    shelves: z.array(servingShelfSchema),
    /**
     * What is behind this list: bottles whose window closes within three years,
     * counted and not named.
     *
     * It is the answer to *is that really all*, and on this cellar it is 31
     * bottles against one — so the sentence it feeds is most of what the page
     * says on most days.
     */
    waiting: z.object({
        wines: z.number().int(),
        bottles: z.number().int(),
    }),
    /** Bottles already standing in the serving cabinets, all reasons together. */
    atServing: z.number().int(),
    /** Bottles in the ageing cabinets, so the empty page can say what it looked at. */
    ageing: z.number().int(),
});
//# sourceMappingURL=serving.js.map